import { useEffect, useState } from 'react';
import { Link } from 'react-admin';
import { Chip, Tooltip } from '@mui/material';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import axiosNest from '../api/axiosNest';
import { buildListUrl } from './dashboard/listUrl';
import { hasToken, useSessionReady } from '../hooks/useSessionReady';

/**
 * Licznik bezczynnych zleceń — w pasku górnym (869f1fzha).
 *
 * Wzorzec „licznika eskalacji w nagłówku aplikacji" ze źródła, ale liczony
 * po słowniku stanów: próg jest konfiguracją (`OrderStatus.idleThresholdMin`),
 * a regułę liczy API tym samym warunkiem, co filtr listy. Front nie ma
 * własnej wersji reguły — pokazuje liczbę i prowadzi do listy z filtrem.
 *
 * Zero i błąd pobrania ukrywają licznik: pusty chip w pasku byłby szumem,
 * a komunikat o błędzie przy każdym odświeżeniu — hałasem.
 *
 * Bez sesji nie pyta wcale (`useSessionReady`) — na ekranie logowania każde
 * zapytanie kończyło się 401.
 */

/** Co ile odświeżamy. Próg liczy się w minutach, więc częściej nie ma sensu. */
export const REFRESH_MS = 5 * 60_000;

export const IDLE_COUNT_URL = '/user-offers/idle-count';

/**
 * Dymek licznika — pełnym zdaniem zamiast „Zlecenia bez działania dłużej
 * niż próg — otwórz listę” (poprawa brzmienia 01.10.2026).
 */
export const IDLE_COUNTER_HINT =
  'Zlecenia, którymi nikt się nie zajął w czasie wyznaczonym dla ich stanu. ' +
  'Kliknij, aby zobaczyć listę.';

export const IdleOrdersCounter = () => {
  const [count, setCount] = useState<number | null>(null);
  const ready = useSessionReady();

  useEffect(() => {
    if (!ready) {
      setCount(null);
      return undefined;
    }
    let active = true;
    const load = () => {
      // Token mógł zniknąć między odświeżeniami (wylogowanie w innej karcie).
      if (!hasToken()) return Promise.resolve();
      return axiosNest
        .get<{ count?: unknown }>(IDLE_COUNT_URL)
        .then((r) => {
          if (!active) return;
          const n = r.data?.count;
          setCount(typeof n === 'number' && Number.isFinite(n) ? n : null);
        })
        .catch(() => {
          if (active) setCount(null);
        });
    };
    void load();
    const timer = setInterval(() => void load(), REFRESH_MS);
    return () => {
      active = false;
      clearInterval(timer);
    };
  }, [ready]);

  if (count === null || count === 0) return null;

  // „Bezczynne”, jak filtr listy i kafelek pulpitu (poprawa brzmienia 01.10.2026).
  return (
    <Tooltip title={IDLE_COUNTER_HINT}>
      <Link to={buildListUrl('user-offers', { idle: true })}>
        <Chip
          size="small"
          color="warning"
          clickable
          icon={<WarningAmberIcon />}
          label={`Bezczynne: ${count}`}
          sx={{ mr: 1 }}
        />
      </Link>
    </Tooltip>
  );
};
